import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { addDays, isIsoDate, todayInTimezone } from '@gd/shared/dates'

export const DATE_RANGE_PRESETS = ['4w', '12w', '6m', '1y', 'custom'] as const
export type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number]

const PRESET_DAYS: Record<Exclude<DateRangePreset, 'custom'>, number> = {
  '4w': 28,
  '12w': 84,
  '6m': 182,
  '1y': 365,
}

export interface DateRange {
  from: string
  to: string
  /** The preset that produced this range, or "custom" when from/to were set directly. */
  preset: DateRangePreset
}

export interface UseDateRangeResult extends DateRange {
  setPreset: (preset: Exclude<DateRangePreset, 'custom'>) => void
  setCustomRange: (from: string, to: string) => void
}

/**
 * Global date range, held in the URL search params (`from`/`to`) so it survives navigation
 * and is shareable. Defaults to the last 12 weeks ending today when absent or invalid.
 */
export function useDateRange(timezone = 'America/Chicago'): UseDateRangeResult {
  const [searchParams, setSearchParams] = useSearchParams()

  const range = useMemo<DateRange>(() => {
    const today = todayInTimezone(timezone)
    const fromParam = searchParams.get('from')
    const toParam = searchParams.get('to')
    if (
      fromParam &&
      toParam &&
      isIsoDate(fromParam) &&
      isIsoDate(toParam) &&
      fromParam <= toParam
    ) {
      const spanDays = Math.round(
        (new Date(toParam).getTime() - new Date(fromParam).getTime()) / 86_400_000,
      )
      const matchedPreset = (
        Object.entries(PRESET_DAYS) as [Exclude<DateRangePreset, 'custom'>, number][]
      ).find(([, days]) => days === spanDays && toParam === today)
      return { from: fromParam, to: toParam, preset: matchedPreset?.[0] ?? 'custom' }
    }
    return { from: addDays(today, -PRESET_DAYS['12w']), to: today, preset: '12w' }
  }, [searchParams, timezone])

  const setPreset = useCallback(
    (preset: Exclude<DateRangePreset, 'custom'>) => {
      const today = todayInTimezone(timezone)
      const next = new URLSearchParams(searchParams)
      next.set('from', addDays(today, -PRESET_DAYS[preset]))
      next.set('to', today)
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams, timezone],
  )

  const setCustomRange = useCallback(
    (from: string, to: string) => {
      const next = new URLSearchParams(searchParams)
      next.set('from', from)
      next.set('to', to)
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  return { ...range, setPreset, setCustomRange }
}
